import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Loader } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useUser } from '@/features/auth/hooks/use-user';
import { updateDocument } from '@/features/documents/api/update-documents';
import { uploadDocument } from '@/features/documents/api/upload-documents';
import {
  FileType,
  FileTypeOptions,
  getFileType,
  useFileTypeDisplay,
} from '@/features/documents/util/mimetype-util';
import { useCurrentOrg } from '@/features/org/hooks/use-org';
import {
  getProjectDetailsQueryKey,
  useProjectDetails,
} from '@/features/projects/shared/hooks/use-project-details';
import {
  getProjectSubprojectsQueryKey,
  useProjectSubprojects,
} from '@/features/projects/shared/hooks/use-project-subprojects';
import { useProjects } from '@/features/projects/shared/hooks/use-projects';
import { usePaywall } from '@/features/subscriptions/hooks/use-paywall';
import ArrowUpIcon from '@/shared/assets/arrow-up.svg?react';
import SaveIcon from '@/shared/assets/save_icon.svg?react';
import ItemHighlighter from '@/shared/components/card-elements/item-highlighter';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { Button } from '@/shared/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  DocumentData,
  DocumentVisibility,
  FileDetails,
  useVisbilityFromString,
  VisbilityOptions,
} from '@/shared/components/ui/upload/document-upload-dialog';
import { FileInputField } from '@/shared/components/ui/upload/file-input-field';
import VisbilityIndicator from '@/shared/components/visibility-indicator';
import { useToast } from '@/shared/hooks/use-toast';
import { ProjectDocument, ProjectDocumentUpdateDto } from '@/shared/types/api';
import { useErrorTranslation } from '@/shared/utils/error-handling';

export const useDocumentMutations = () => {
  const { t } = useTranslation('document', { keyPrefix: 'FileInputForm' });
  const { toast } = useToast();
  const { openPaywallFromError } = usePaywall();
  const errorTranslation = useErrorTranslation();

  const uploadMutation = useMutation({
    mutationFn: uploadDocument,
    onError: async (error) => {
      const handled = await openPaywallFromError(error);
      if (!handled) {
        const translated = errorTranslation(error);
        toast({
          title: translated,
          description: error.message,
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateDocument,
    onError: (error) => {
      toast({
        title: t('errors.update_error'),
        description: error.message,
      });
    },
  });

  return {
    uploadDocument: uploadMutation,
    updateDocument: updateMutation,
  };
};

export type UpdateDocumentData = {
  updateDto: ProjectDocumentUpdateDto;
  shouldUpdatePath: boolean;
  oldPath: string | undefined;
};

export type CreateDocumentData = {
  fileDetails: FileDetails;
  orgId: number;
};

type FileInputFormProps = {
  file?: File | null;
  document?: DocumentData;
  editMode?: boolean;
  onCloseDialog: () => void;
  projectId?: number;
  onSubmitSuccess: (data: ProjectDocument) => void;
  handleFileChange: (file: File | null) => void;
  publicOnly?: boolean;
  onCustomSubmit?: (data: CreateDocumentData) => void;
  onCustomEdit?: (data: UpdateDocumentData) => void;
};

const FileInputForm = ({
  file,
  document,
  editMode = false,
  onCloseDialog,
  projectId,
  onSubmitSuccess,
  handleFileChange,
  publicOnly = false,
  onCustomSubmit,
  onCustomEdit,
}: FileInputFormProps) => {
  const { t } = useTranslation('document', { keyPrefix: 'FileInputForm' });
  const formSchema = z.object({
    fileName: z.string(),
    project: z.number().min(1, t('errors.project_required')),
    subproject: z.number().nullable().optional(),
    partner: z.string().nullable().optional(),
    kpi: z.string().nullable().optional(),
    type: z.string(),
    visibility: z.enum(VisbilityOptions),
    association: z.string(),
  });
  //Data fetching
  const { data: user } = useUser();
  const { data: projects } = useProjects();

  const { data: org } = useCurrentOrg();

  const { data: projectDetails } = useProjectDetails({
    projectId: projectId?.toString() ?? '',
    queryProps: {
      enabled: !!projectId && !document,
      queryKey: getProjectDetailsQueryKey(projectId?.toString() ?? ''),
    },
  });

  // Fetch project details for the document's project_id to resolve parent/subproject hierarchy
  const documentProjectId = document?.project_id;
  const { data: documentProjectDetails } = useProjectDetails({
    projectId: documentProjectId?.toString() ?? '',
    queryProps: {
      enabled: !!documentProjectId && editMode,
      queryKey: getProjectDetailsQueryKey(
        `doc-${documentProjectId?.toString() ?? ''}`,
      ),
    },
  });

  const { uploadDocument, updateDocument } = useDocumentMutations();

  const isSubproject =
    projectDetails &&
    (projectDetails.type === 'subproject_client' ||
      projectDetails.type === 'subproject_contractor');

  const isContractorSubproject =
    projectDetails?.type === 'subproject_contractor';

  // Check if the document's project is a subproject
  const isDocumentProjectSubproject =
    documentProjectDetails &&
    (documentProjectDetails.type === 'subproject_client' ||
      documentProjectDetails.type === 'subproject_contractor');

  const defaultProject = document?.project_id
    ? isDocumentProjectSubproject
      ? documentProjectDetails.parent_id!
      : document.project_id
    : isSubproject
      ? projectDetails.parent_id!
      : (projectId ?? 0);

  const defaultSubproject = document?.project_id
    ? isDocumentProjectSubproject
      ? document.project_id
      : null
    : isSubproject
      ? projectId!
      : null;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fileName: document?.title ?? file?.name ?? '',
      project: defaultProject,
      subproject: defaultSubproject,
      type: getFileType(document?.type ?? file?.type ?? ''),
      visibility: (document?.visibility as DocumentVisibility) ?? 'public',
      association: document?.association ?? '',
    },
  });

  useEffect(() => {
    if (projectDetails && !document && projectId) {
      const isSubproject =
        projectDetails.type === 'subproject_client' ||
        projectDetails.type === 'subproject_contractor';

      if (isSubproject) {
        form.setValue('project', projectDetails.parent_id!);
        form.setValue('subproject', projectId);
      }
    }
  }, [projectDetails, document, projectId, form]);

  // When editing an existing document, resolve its project hierarchy once details are loaded
  useEffect(() => {
    if (documentProjectDetails && document?.project_id && editMode) {
      const isSubproject =
        documentProjectDetails.type === 'subproject_client' ||
        documentProjectDetails.type === 'subproject_contractor';

      if (isSubproject) {
        form.setValue('project', documentProjectDetails.parent_id!);
        form.setValue('subproject', document.project_id);
      } else {
        form.setValue('project', document.project_id);
      }
    }
  }, [documentProjectDetails, document, editMode, form]);

  const selectedProjectId = form.watch('project');
  const selectedProject = projects?.find((p) => p.id === selectedProjectId);
  const isMainProject = selectedProject?.type === 'project';

  const { data: subprojects } = useProjectSubprojects({
    projectId: selectedProjectId,
    queryProps: {
      enabled: !!selectedProjectId && selectedProjectId !== 0 && isMainProject,
      queryKey: getProjectSubprojectsQueryKey({ projectId: selectedProjectId }),
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (user?.id === undefined || user?.id === null) {
      //TODO handle this case or remove it once route guards exist
      throw new Error('User id must be defined');
    }

    const visibilityChanged = document?.visibility !== values.visibility;
    const finalProjectId = values.subproject ?? values.project;

    if (editMode) {
      const isNewDocument = !document?.id || document?.id === 0;

      if (isNewDocument) {
        const createData: CreateDocumentData = {
          fileDetails: {
            file: file!,
            title: values.fileName,
            project: finalProjectId,
            partner: values.partner,
            kpi: values.kpi,
            type: values.type,
            visibility: values.visibility,
            owner: user!.id,
            date: new Date(),
            association: values.association,
          },
          orgId: org!.id,
        };

        if (onCustomSubmit) {
          onCustomSubmit(createData);
          onCloseDialog();
          return;
        }

        uploadDocument.mutate(
          {
            fileDetails: createData.fileDetails,
            orgId: createData.orgId,
          },
          {
            onSuccess: (data) => {
              onSubmitSuccess(data);
              onCloseDialog();
            },
          },
        );
      } else {
        // For existing documents, proceed with normal update
        const shouldUpdatePath =
          document?.project_id !== finalProjectId ||
          document?.org_id !== org?.id ||
          visibilityChanged;

        const updateData: UpdateDocumentData = {
          updateDto: {
            id: document?.id,
            title: values.fileName,
            project_id: finalProjectId,
            type: values.type,
            visibility: values.visibility,
            association: values.association,
            org_id: org!.id,
            creator_id: document?.creator_id,
          },
          shouldUpdatePath: shouldUpdatePath,
          oldPath: document?.file_path,
        };

        if (onCustomEdit) {
          onCustomEdit(updateData);
          onCloseDialog();
          return;
        }

        updateDocument.mutate(
          {
            updateDto: updateData.updateDto,
            shouldUpdatePath: updateData.shouldUpdatePath,
            oldPath: updateData.oldPath,
          },
          {
            onSuccess: (data) => {
              onSubmitSuccess(data);
              onCloseDialog();
            },
          },
        );
      }
    } else {
      if (org == null) return;
      const createData: CreateDocumentData = {
        fileDetails: {
          file: file!,
          title: values.fileName,
          project: finalProjectId,
          partner: values.partner,
          kpi: values.kpi,
          type: values.type,
          visibility: values.visibility,
          owner: user!.id,
          date: new Date(),
          association: values.association,
        },
        orgId: org!.id,
      };

      if (onCustomSubmit) {
        onCustomSubmit(createData);
        onCloseDialog();
        return;
      }

      uploadDocument.mutate(
        {
          fileDetails: createData.fileDetails,
          orgId: createData.orgId,
        },
        {
          onSuccess: (data) => {
            onSubmitSuccess(data);
            onCloseDialog();
          },
        },
      );
    }
  }

  const TitelInput = (
    <FormField
      control={form.control}
      name="fileName"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <LabelInput
              label={t('title_input.label')}
              placeholder={t('title_input.placeholder')}
              {...field}
              value={field.value ?? ''}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );

  const AssociationInput = (
    <FormField
      control={form.control}
      name="association"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <LabelInput
              label={t('association_input.label')}
              placeholder={t('association_input.placeholder')}
              {...field}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
  const ProjectInput = (
    <FormField
      control={form.control}
      name="project"
      render={({ field }) => {
        // Get project name for display - use parent name in contractor subproject context
        const displayedProjectName =
          isContractorSubproject && projectDetails?.parent?.name
            ? projectDetails.parent.name
            : projects?.find((project) => project.id === field.value)?.name;

        return (
          <FormItem>
            <FormControl>
              <Select
                disabled={projectId != null && !editMode}
                value={field.value?.toString() ?? ''}
                onValueChange={(value) => {
                  field.onChange(parseInt(value));
                  form.setValue('subproject', null);
                }}
              >
                <SelectTrigger isInputTrigger>
                  <SelectValue
                    asLabelInput
                    label={t('project_input.label')}
                    className="placeholder:text-white"
                    placeholder={t('project_input.placeholder')}
                  >
                    {displayedProjectName}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {projects && projects.length > 0
                    ? projects
                        .filter(
                          (project) =>
                            project.type === 'project' ||
                            ((projectId == null || editMode) &&
                              project.type === 'subproject_contractor'),
                        )
                        .map((project) => (
                          <SelectItem
                            key={project.id}
                            value={project.id.toString()}
                          >
                            {project.name}
                          </SelectItem>
                        ))
                    : [
                        <SelectItem disabled key={'empty'} value={'empty'}>
                          {t('project_input.no_projects')}
                        </SelectItem>,
                      ]}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );

  const SubprojectInput = (
    <FormField
      control={form.control}
      name="subproject"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <Select
              disabled={!selectedProjectId || selectedProjectId === 0}
              value={field.value?.toString() ?? ''}
              onValueChange={(value) => {
                field.onChange(value ? parseInt(value) : null);
              }}
            >
              <SelectTrigger isInputTrigger>
                <SelectValue
                  asLabelInput
                  label={t('subproject_input.label')}
                  className="placeholder:text-white"
                  placeholder={t('subproject_input.placeholder')}
                >
                  {
                    subprojects?.find(
                      (subproject) => subproject.id === field.value,
                    )?.name
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {subprojects && subprojects.length > 0 ? (
                  subprojects.map((subproject) => (
                    <SelectItem
                      key={subproject.id}
                      value={subproject.id.toString()}
                    >
                      {subproject.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem disabled key={'empty'} value={'empty'}>
                    {t('subproject_input.no_subprojects')}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  // Not used atm. Maybe in the future
  // const PartnerInput = (
  //   <FormField
  //     control={form.control}
  //     name="partner"
  //     render={({ field }) => (
  //       <FormItem>
  //         <FormControl>
  //           <Select
  //             disabled={true}
  //             defaultValue={form.getValues('partner') ?? ''}
  //             onValueChange={(value) => {
  //               field.onChange(value);
  //             }}
  //           >
  //             <SelectTrigger isInputTrigger>
  //               <SelectValue
  //                 aria-disabled
  //                 asLabelInput
  //                 label={t('partner_input.label')}
  //                 className="placeholder:text-white"
  //                 placeholder={t('partner_input.placeholder')}
  //               />
  //             </SelectTrigger>

  //             <SelectContent>
  //               {['Partner 1', 'Partner 2'].map((value) => (
  //                 <SelectItem key={value} value={value}>
  //                   {value}
  //                 </SelectItem>
  //               ))}
  //             </SelectContent>
  //           </Select>
  //         </FormControl>
  //       </FormItem>
  //     )}
  //   />
  // );

  // const KpiInput = (
  //   <FormField
  //     control={form.control}
  //     name="kpi"
  //     render={({ field }) => (
  //       <FormItem>
  //         <FormControl>
  //           <Select
  //             disabled
  //             value={field.value ?? ''}
  //             onValueChange={field.onChange}
  //           >
  //             <SelectTrigger isInputTrigger>
  //               <SelectValue
  //                 aria-disabled
  //                 asLabelInput
  //                 label={t('kpi_input.label')}
  //                 placeholder={t('kpi_input.placeholder')}
  //               >
  //                 {field.value}
  //               </SelectValue>
  //             </SelectTrigger>
  //             <SelectContent>{[]}</SelectContent>
  //           </Select>
  //         </FormControl>
  //       </FormItem>
  //     )}
  //   />
  // );

  // console.log('document', document);
  // console.log('file', file);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-5"
      >
        <FileInputField
          file={file}
          document={document}
          handleFileChange={(file) => {
            if (file) {
              form.setValue('fileName', file.name);
              form.setValue('type', getFileType(file.type));
            } else {
              form.setValue('fileName', '');
              form.setValue('type', '');
            }
            handleFileChange(file);
          }}
        />
        {TitelInput}
        {AssociationInput}
        {ProjectInput}
        {(!isContractorSubproject || editMode) && SubprojectInput}
        {/* {PartnerInput}
        {KpiInput} */}
        {
          <div className="flex flex-row justify-between gap-9 pt-4">
            <VisibilityInput
              currentVisibility={form.watch('visibility')}
              publicOnly={publicOnly}
              onChange={(visibility) => {
                form.setValue('visibility', visibility);
              }}
            />
            {/* Removed until needed */}
            {/* <TypeInput
              currentType={form.watch('type') as FileType}
              onChange={(type) => {
                form.setValue('type', type);
              }}
            /> */}
          </div>
        }
        <div className="flex justify-end pt-3">
          <Button
            variant={'greenGradient'}
            type="submit"
            className="text-black"
          >
            {!updateDocument.isPending &&
              !uploadDocument.isPending &&
              (editMode ? (
                <SaveIcon className="!size-[18px]" />
              ) : (
                <ArrowUpIcon className="!size-[18px]" />
              ))}
            {editMode ? (
              !updateDocument.isPending ? (
                t('action.update')
              ) : (
                <LoadingIndicator />
              )
            ) : !uploadDocument.isPending ? (
              t('action.upload')
            ) : (
              <>
                <Loader className="animate-spin" />
                {t('action.loading')}
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default FileInputForm;

const VisibilityInput = ({
  currentVisibility,
  publicOnly = false,
  onChange,
}: {
  currentVisibility: DocumentVisibility;
  publicOnly?: boolean;
  onChange: (visibility: DocumentVisibility) => void;
}) => {
  const { t } = useTranslation('document', {
    keyPrefix: 'FileInputForm',
  });
  const { translateVisibility } = useVisbilityFromString();

  const items = VisbilityOptions.map((option) => ({
    id: option,
    title: translateVisibility(option),
    icon: <VisbilityIndicator visibility={option} showRawIcon={true} />,
    subtitle: t('visibility_input.label'),
  })).filter((item) => {
    if (!publicOnly) return true;
    return item.id === 'public';
  });

  const currItem = {
    id: currentVisibility,
    title: translateVisibility(currentVisibility),
    subtitle: t('visibility_input.label'),
    icon: (
      <VisbilityIndicator visibility={currentVisibility} showRawIcon={true} />
    ),
  };

  return (
    <ItemHighlighter
      onChange={(item) => {
        if (publicOnly) return;
        onChange(item.id as DocumentVisibility);
      }}
      items={items}
      selectedItem={currItem}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TypeInput = ({
  currentType,
  onChange,
}: {
  currentType: FileType;
  onChange: (type: FileType) => void;
}) => {
  const { t } = useTranslation('document', {
    keyPrefix: 'FileInputForm',
  });

  const { getFileTypeDisplay } = useFileTypeDisplay();

  const items = FileTypeOptions.map((option) => ({
    id: option,
    title: getFileTypeDisplay(option).label,
    icon: getFileTypeDisplay(option).icon,
    subtitle: t('type_input.label'),
  }));

  const currItem = {
    id: currentType,
    title: getFileTypeDisplay(currentType).label,
    subtitle: t('type_input.label'),
    icon: getFileTypeDisplay(currentType).icon,
  };

  return (
    <ItemHighlighter
      items={items}
      selectedItem={currItem}
      onChange={(item) => {
        onChange(item.id as FileType);
      }}
    />
  );
};
